import { Suspense, lazy, useEffect, useState, useTransition } from 'react';
import { priorityLoaderExec } from './priorityLoader';
import Raven from 'raven-js';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_COMPONENT = () => /*#__PURE__*/_jsx(_Fragment, {});

/**
 * Use this component if you want to render a component in a way that does not hurt initial page load performance.
 *
 * It internally will use priorityLoader to determine when to render the children.
 * When it is time to render children, it will:
 * - dynamically import `loader` with React.Lazy
 * - make a state update to render the children, marked as non-blocking with react's useTransition hook.
 * - render the component with React.Lazy and React.Suspense
 *
 * Example:
 * ```tsx
 * import { PriorityRender } from 'path/to/PriorityRender';
 * const loadMyComponent = () =>
 *   import(
 *     \/* webpackChunkName: "my-component" *\/ 'my-component'
 *   );
 *
 * const MyLoadingComponent = () => <div>Loading...</div>;
 *
 * export const MyAsyncComponent = () => {
 *   return (
 *     <PriorityRender
 *       priority="low"
 *       LoadingComponent={MyLoadingComponent}
 *       loader={loadMyComponent}
 *       LOGGING_COMPONENT_NAME="MyAsyncComponent" // This is used for error tracking, make your error name sourcegraph unique
 *     />
 *   );
 * };
 * ```
 */
export const PriorityRender = ({
  priority,
  LoadingComponent = EMPTY_COMPONENT,
  loader,
  LOGGING_COMPONENT_NAME
}) => {
  const Component = /*#__PURE__*/lazy(loader);
  const [rendered, setRendered] = useState(false);
  const [isPending, startTransition] = useTransition();
  useEffect(() => {
    priorityLoaderExec({
      priority,
      loader: () => {
        startTransition(() => {
          setRendered(true);
        });
      }
    }).catch(e => {
      Raven.captureException(e, {
        extra: {
          priorityRender: true,
          LOGGING_COMPONENT_NAME
        }
      });
    });
  }, [LOGGING_COMPONENT_NAME, priority, startTransition]);
  if (isPending || !rendered) {
    return /*#__PURE__*/_jsx(LoadingComponent, {});
  }
  return /*#__PURE__*/_jsx(Suspense, {
    fallback: /*#__PURE__*/_jsx(LoadingComponent, {}),
    children: /*#__PURE__*/_jsx(Component, {})
  });
};